import {Typography} from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';

import MazeThumbnail from '@cdo/apps/code-studio/components/lessonExtras/MazeThumbnail';
import fontConstants from '@cdo/apps/fontConstants';
import FontAwesome from '@cdo/apps/legacySharedComponents/FontAwesome';
import LessonExtrasFlagIcon from '@cdo/apps/templates/progress/LessonExtrasFlagIcon';
import ProgressBubble from '@cdo/apps/templates/progress/ProgressBubble';
import {getIconForLevel} from '@cdo/apps/templates/progress/progressHelpers';
import {levelType} from '@cdo/apps/templates/progress/progressTypes';
import SafeMarkdown from '@cdo/apps/templates/SafeMarkdown';
import color from '@cdo/apps/util/color';

export default class SublevelCard extends React.Component {
  static propTypes = {
    isLessonExtra: PropTypes.bool,
    // sublevels generally use "perfect" instead of status
    sublevel: levelType,
    sectionId: PropTypes.number,
    userId: PropTypes.number,
  };

  getSublevelUrl = () => {
    const {isLessonExtra, sublevel, sectionId, userId} = this.props;

    if (isLessonExtra) {
      const baseUrl = location.origin + location.pathname + '?';
      if (sectionId && userId) {
        // Both sectionId and userId are required to link to a student's work on a bonus level.
        return (
          baseUrl +
          queryString.stringify({
            id: sublevel.id,
            section_id: sectionId,
            user_id: userId,
          })
        );
      } else if (sectionId) {
        return (
          baseUrl +
          queryString.stringify({
            id: sublevel.id,
            section_id: sectionId,
          })
        );
      }
      return baseUrl + queryString.stringify({id: sublevel.id});
    }

    return sublevel.url + location.search;
  };

  renderWithMazeThumbnail() {
    return (
      <MazeThumbnail
        size={THUMBNAIL_IMAGE_SIZE}
        mazeSummary={this.props.sublevel.maze_summary}
      />
    );
  }

  renderThumbnail = () => {
    const {sublevel} = this.props;
    if (sublevel.thumbnail_url) {
      // TODO: A11y279 (https://codedotorg.atlassian.net/browse/A11Y-279)
      // Verify or update this alt-text as necessary
      return (
        <img src={sublevel.thumbnail_url} style={styles.thumbnail} alt="" />
      );
    } else if (['Maze', 'Karel'].includes(sublevel.type)) {
      return this.renderWithMazeThumbnail();
    } else {
      // Render a square-shaped placeholder if we don't have a thumbnail.
      return (
        <div style={styles.placeholderThumbnail} className="placeholder">
          <FontAwesome
            icon={getIconForLevel(sublevel)}
            style={styles.icon}
            key={sublevel.id}
          />
        </div>
      );
    }
  };

  /* Determine if we should use the normal progress
   * bubble or the lesson extras flag.
   */
  renderBubble = () => {
    const {isLessonExtra, sublevel} = this.props;

    if (isLessonExtra) {
      return <LessonExtrasFlagIcon isPerfect={sublevel.perfect} size={30} />;
    }

    let mappedSublevel = sublevel;
    if (mappedSublevel) {
      // ProgressBubble expects level keys to be camelCase (instead of snake case)
      // converting keys to the correct casing here
      mappedSublevel = _.mapKeys(sublevel, (value, key) => _.camelCase(key));
    }

    return (
      <ProgressBubble
        level={mappedSublevel}
        disabled={true}
        hideToolTips={true}
      />
    );
  };

  render() {
    const {sublevel} = this.props;

    return (
      <a href={this.getSublevelUrl()}>
        <div
          key={sublevel.id}
          style={styles.row}
          className="uitest-bubble-choice"
        >
          {this.renderThumbnail()}
          <div
            style={{
              ...styles.column,
              ...{
                width: WIDTH - (MARGIN * 2 + THUMBNAIL_IMAGE_SIZE) + BORDER * 2,
              },
            }}
          >
            <div style={styles.bubbleAndTitle}>
              {this.renderBubble()}
              <Typography
                className="sublevel-card-title-uitest"
                component="h3"
                variant="h6"
              >
                {sublevel.display_name}
              </Typography>
            </div>
            {sublevel.description && (
              <div
                style={styles.description}
                className="sublevel-card-description-uitest"
              >
                <SafeMarkdown markdown={sublevel.description} />
              </div>
            )}
          </div>
        </div>
      </a>
    );
  }
}

const THUMBNAIL_IMAGE_SIZE = 150;
const MARGIN = 10;
const WIDTH = 435;
const BORDER = 1;

const styles = {
  row: {
    display: 'flex',
    width: WIDTH + BORDER * 2,
    marginBottom: MARGIN,
    marginInlineEnd: MARGIN,
    backgroundColor: color.white,
    border: `${BORDER}px solid rgb(187, 187, 187)`,
    borderRadius: 2,
    cursor: 'pointer',
  },
  thumbnail: {
    minWidth: THUMBNAIL_IMAGE_SIZE + MARGIN * 2 + BORDER,
    width: THUMBNAIL_IMAGE_SIZE + MARGIN * 2 + BORDER,
    height: THUMBNAIL_IMAGE_SIZE + MARGIN * 2,
    padding: MARGIN,
    borderInlineEnd: `${BORDER}px solid rgb(187, 187, 187)`,
  },
  placeholderThumbnail: {
    minWidth: THUMBNAIL_IMAGE_SIZE,
    width: THUMBNAIL_IMAGE_SIZE,
    height: THUMBNAIL_IMAGE_SIZE,
    backgroundColor: color.lighter_gray,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `${BORDER}px solid rgb(187, 187, 187)`,
    borderRadius: 2,
  },
  icon: {
    fontSize: THUMBNAIL_IMAGE_SIZE - 50,
    color: color.white,
    opacity: 0.8,
  },
  column: {
    marginLeft: MARGIN * 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: MARGIN,
  },
  bubbleAndTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  title: {
    minHeight: 30,
    fontSize: 16,
    lineHeight: '25px',
    ...fontConstants['main-font-semi-bold'],
    color: color.teal,
    marginBottom: 10,
    marginInlineStart: MARGIN,
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    hyphens: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  description: {
    marginTop: 6,
    marginInlineStart: 4,
    color: color.black,
  },
};
